/**
 * The admin sign-in screen.
 *
 * Shown in place of the admin console rather than at its own address: an
 * operator who followed a link to a panel should land back on that panel once
 * they are in, not on a dashboard having lost where they were going.
 */
import { useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import { login } from '../api/session';

interface LoginPageProps {
  /** Called once the token is stored, so the guard can re-check and let go. */
  onSignedIn: () => void;
}

export function LoginPage({ onSignedIn }: LoginPageProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      await login(username.trim(), password);
      onSignedIn();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not sign in.');
      setBusy(false);
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: 3,
      }}
    >
      <Paper
        component="form"
        onSubmit={(event) => void submit(event)}
        elevation={0}
        variant="outlined"
        sx={{ p: 4, width: '100%', maxWidth: 400 }}
      >
        <Stack spacing={3}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <LockOutlinedIcon fontSize="small" color="action" />
            <Typography variant="h6" component="h1">
              Welcome to TravPlaces!
            </Typography>
          </Stack>

          {error ? <Alert severity="error">{error}</Alert> : null}

          <TextField
            label="Username"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            autoComplete="username"
            autoFocus
            required
            fullWidth
          />

          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            required
            fullWidth
          />

          <Button type="submit" variant="contained" size="large" disabled={busy} fullWidth>
            {busy ? 'Signing in…' : 'Sign in'}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
